import React, { useState, useEffect } from 'react';
import { AdminNavigationDrawer } from '../../components/admin/AdminNavigationDrawer';
import { useOTPAuth } from '../../hooks/useOTPAuth';
import { billingService, Coupon, CouponWithRedemptions } from '@/services/billingService';

export const ManageCouponsPage: React.FC = () => {
  const { user, logout } = useOTPAuth();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<CouponWithRedemptions | null>(null);
  const [showRedemptionsDialog, setShowRedemptionsDialog] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isDrawerPinned, setIsDrawerPinned] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await billingService.getAllCoupons();
      setCoupons(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch coupons';
      setError(errorMessage);
      console.error('Error fetching coupons:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRedemptions = async (couponId: number) => {
    try {
      const data = await billingService.getCouponWithRedemptions(couponId);
      setSelectedCoupon(data);
      setShowRedemptionsDialog(true);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch redemptions';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleDeactivate = async (couponId: number, code: string) => {
    if (!confirm(`Are you sure you want to deactivate coupon "${code}"?`)) {
      return;
    }

    try {
      await billingService.deactivateCoupon(couponId);
      alert('Coupon deactivated successfully');
      fetchCoupons();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to deactivate coupon';
      alert(`Error: ${errorMessage}`);
    }
  };

  return (
    <div className="flex h-screen bg-[#F5F5F5]">
      <AdminNavigationDrawer
        user={user}
        onLogout={logout}
        isMobileOpen={isMobileDrawerOpen}
        onMobileClose={() => setIsMobileDrawerOpen(false)}
        onPinChange={setIsDrawerPinned}
      />

      <div className={`flex-1 flex flex-col ${isDrawerPinned ? 'md:ml-64' : 'md:ml-16'}`}>
        {/* Header */}
        <header className="bg-[#F5F5F5] border-b border-gray-300 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Mobile hamburger menu */}
              <button
                onClick={() => setIsMobileDrawerOpen(true)}
                className="md:hidden p-2 hover:bg-gray-200 rounded transition-colors"
                title="Open menu"
              >
                <svg className="w-6 h-6 text-[#080808]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-2xl font-semibold text-[#080808] tracking-tight">
                Manage Coupons
              </h1>
            </div>
            <div className="text-sm text-gray-500">
              {coupons.length} total coupons
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Create Coupon Button */}
            <div className="mb-6">
              <button
                onClick={() => setShowCreateDialog(true)}
                className="px-6 py-3 bg-[#74AA9C] text-white rounded-lg hover:bg-[#5C8A7E] transition-colors font-medium"
              >
                Create Coupon
              </button>
            </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Coupons Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <img src="/images/logos/loader-circle.svg" alt="Loading..." className="h-12 w-12" />
          </div>
        ) : coupons.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-[#4A4A4A] mb-4">No coupons created yet</p>
            <button
              onClick={() => setShowCreateDialog(true)}
              className="px-6 py-2 bg-[#74AA9C] text-white rounded-lg hover:bg-[#5C8A7E] transition-colors"
            >
              Create First Coupon
            </button>
          </div>
        ) : (
          <>
            {/* Card view for mobile/tablet (< 1280px) */}
            <div className="xl:hidden space-y-4">
              {coupons.map((coupon) => (
                <div key={coupon.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="font-mono font-semibold text-[#080808] text-base break-words">{coupon.code}</div>
                      {coupon.description && (
                        <div className="text-sm text-[#4A4A4A] mt-1">{coupon.description}</div>
                      )}
                    </div>
                    <div className="ml-3">
                      {coupon.is_active ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 whitespace-nowrap">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 whitespace-nowrap">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-gray-500 uppercase mb-1">Credit Amount</div>
                      <div className="text-lg font-semibold text-[#74AA9C]">
                        ${coupon.credit_amount_usd.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase mb-1">Redemptions</div>
                      <div className="text-sm text-[#080808]">
                        {coupon.redemption_count}
                        {coupon.max_redemptions && ` / ${coupon.max_redemptions}`}
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 mb-3">
                    Created: {new Date(coupon.created_at).toLocaleDateString()}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewRedemptions(coupon.id)}
                      className="flex-1 px-4 py-2 bg-[#74AA9C] text-white rounded-md hover:bg-[#5C8A7E] transition-colors text-sm"
                    >
                      View Redemptions
                    </button>
                    {coupon.is_active && (
                      <button
                        onClick={() => handleDeactivate(coupon.id, coupon.code)}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Table view for desktop (>= 1280px) */}
            <div className="hidden xl:block bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 2xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-3 2xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-3 2xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Used
                    </th>
                    <th className="px-3 2xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="hidden 2xl:table-cell px-3 2xl:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-3 2xl:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {coupons.map((coupon) => (
                    <tr key={coupon.id} className="hover:bg-gray-50">
                      <td className="px-3 2xl:px-6 py-3 2xl:py-4 whitespace-nowrap">
                        <div className="font-mono font-semibold text-[#080808] text-sm 2xl:text-base">{coupon.code}</div>
                        {coupon.description && (
                          <div className="text-xs 2xl:text-sm text-[#4A4A4A] mt-1">{coupon.description}</div>
                        )}
                      </td>
                      <td className="px-3 2xl:px-6 py-3 2xl:py-4 whitespace-nowrap">
                        <div className="text-base 2xl:text-lg font-semibold text-[#74AA9C]">
                          ${coupon.credit_amount_usd.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-3 2xl:px-6 py-3 2xl:py-4 whitespace-nowrap">
                        <div className="text-xs 2xl:text-sm text-[#080808]">
                          {coupon.redemption_count}
                          {coupon.max_redemptions && ` / ${coupon.max_redemptions}`}
                        </div>
                      </td>
                      <td className="px-3 2xl:px-6 py-3 2xl:py-4 whitespace-nowrap">
                        {coupon.is_active ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="hidden 2xl:table-cell px-3 2xl:px-6 py-3 2xl:py-4 whitespace-nowrap text-xs 2xl:text-sm text-[#4A4A4A]">
                        {new Date(coupon.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 2xl:px-6 py-3 2xl:py-4 whitespace-nowrap text-right text-xs 2xl:text-sm font-medium">
                        <div className="flex flex-col xl:flex-row gap-1 xl:gap-0 items-end xl:items-center justify-end">
                          <button
                            onClick={() => handleViewRedemptions(coupon.id)}
                            className="text-[#74AA9C] hover:text-[#5C8A7E] xl:mr-3 2xl:mr-4 whitespace-nowrap"
                          >
                            <span className="2xl:hidden">View</span>
                            <span className="hidden 2xl:inline">View Redemptions</span>
                          </button>
                          {coupon.is_active && (
                            <button
                              onClick={() => handleDeactivate(coupon.id, coupon.code)}
                              className="text-red-600 hover:text-red-900 whitespace-nowrap"
                            >
                              Deactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            </div>
          </>
        )}
          </div>
        </main>

        {/* Create Coupon Dialog */}
        {showCreateDialog && (
          <CreateCouponDialog
            onClose={() => setShowCreateDialog(false)}
            onSuccess={() => {
              setShowCreateDialog(false);
              fetchCoupons();
            }}
          />
        )}

        {/* Redemptions Dialog */}
        {showRedemptionsDialog && selectedCoupon && (
          <RedemptionsDialog
            coupon={selectedCoupon}
            onClose={() => {
              setShowRedemptionsDialog(false);
              setSelectedCoupon(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Create Coupon Dialog Component
const CreateCouponDialog: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const [code, setCode] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [description, setDescription] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await billingService.createCoupon({
        code: code.toUpperCase(),
        credit_amount: parseFloat(creditAmount),
        description: description || undefined,
        max_redemptions: maxRedemptions ? parseInt(maxRedemptions) : undefined,
      });
      onSuccess();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create coupon';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <h2 className="text-2xl font-bold text-[#080808] mb-4">Create New Coupon</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#080808] mb-2">
                Coupon Code *
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="WELCOME50"
                required
                disabled={loading}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#74AA9C] font-mono"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#080808] mb-2">
                Credit Amount (USD) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="10.00"
                required
                disabled={loading}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#080808] mb-2">
                Description
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Welcome bonus for new users"
                disabled={loading}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#080808] mb-2">
                Max Redemptions (optional)
              </label>
              <input
                type="number"
                min="1"
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(e.target.value)}
                placeholder="Leave empty for unlimited"
                disabled={loading}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#74AA9C]"
              />
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 text-[#080808] rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-[#74AA9C] text-white rounded-md hover:bg-[#5C8A7E] transition-colors disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Coupon'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Redemptions Dialog Component
const RedemptionsDialog: React.FC<{
  coupon: CouponWithRedemptions;
  onClose: () => void;
}> = ({ coupon, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-[#080808]">Redemption History</h2>
          <p className="text-[#4A4A4A] mt-1">
            Coupon: <span className="font-mono font-semibold">{coupon.code}</span> - $
            {coupon.credit_amount_usd.toFixed(2)}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {coupon.redemptions.length === 0 ? (
            <div className="text-center text-[#4A4A4A] py-8">
              No redemptions yet for this coupon.
            </div>
          ) : (
            <>
              {/* Card view for mobile/tablet (< 1280px) */}
              <div className="xl:hidden space-y-3">
                {coupon.redemptions.map((redemption) => (
                  <div key={redemption.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold text-[#080808] text-sm">{redemption.user_name}</div>
                        <div className="text-xs text-[#4A4A4A] mt-1">{redemption.user_email || 'N/A'}</div>
                      </div>
                      <div className="text-base font-semibold text-[#74AA9C] ml-2">
                        ${redemption.credit_amount_usd.toFixed(2)}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(redemption.redeemed_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              {/* Table view for desktop (>= 1280px) */}
              <div className="hidden xl:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        User
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Email
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Credit Amount
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Redeemed At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {coupon.redemptions.map((redemption) => (
                      <tr key={redemption.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-[#080808]">{redemption.user_name}</td>
                        <td className="px-4 py-3 text-sm text-[#4A4A4A]">
                          {redemption.user_email || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-[#74AA9C]">
                          ${redemption.credit_amount_usd.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#4A4A4A]">
                          {new Date(redemption.redeemed_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-[#080808] rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
